import {z} from 'zod'
import {processFetchJsonResponse, uri, UriParts} from 'shared/uri'
import {ResultsQuery as RQ} from 'shared/forecast/results/QuestionResults'
import {env} from 'utils/env'
import {DateTime} from 'luxon'
import {apiClient} from 'shared/apiClient'
import {queryStringSerializer} from 'shared/queryStringSerializer'
import {hashString} from 'shared/text'
import {asyncMap} from 'modern-async'
import {pick} from 'remeda'

export namespace Publish {
  export const client = apiClient({
    md: {
      publish: {
        zReq: z.object({
          question_id: z.string(),
          publish: z.boolean(),
          ts: z.coerce.number()
        }),
        zResOk: z.object({ts: z.coerce.number().optional()})
      }
    },
    url: uri('/api/publish')
  } as const)

  export const publish = client.makePost('publish')
  export type PublishRequest = Parameters<typeof publish>[0]
  export type PublishResult = Awaited<ReturnType<typeof publish>>

  type UrlPropsCommon = {id: string; ts?: number; query?: RQ.ResultsQuery}
  type UrlPropsLiveView = {type: 'liveView' | undefined; workspace_id: string} & UrlPropsCommon
  export type UrlPropsPublished = {type: 'published'} & UrlPropsCommon
  export type UrlProps = UrlPropsLiveView | UrlPropsPublished

  export const vizUrl = (props?: {
    /** the url of the page being used so we can have intelligent defaults for the viz host without needing to define NEXT_PUBLIC_VIZ_URL  */
    contextUrl?: string
  }) => {
    if (env.NEXT_PUBLIC_VIZ_URL) return env.NEXT_PUBLIC_VIZ_URL
    const contextUrl = props?.contextUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined)
    if (contextUrl) {
      const url = uri(contextUrl)
      if (url.host) {
        // if this is a local path (i.e. lopback of local host, then point to http://localhost:4004 as that's the default forour dev setup
        if (url.hostSansPort == 'localhost' || url.hostSansPort == '127.0.0.1')
          return uri({
            ...pick(url, ['protocol']),
            host: `${url.hostSansPort}:4004`
          }).url
        // for previews on vercel, we can assume the equivalent one for the embed project.
        if (url.host.startsWith('forecast-git-')) {
          const embedHost = url.host.replace(/^forecast-git-/, 'embed-git-')
          return uri({
            ...pick(url, ['protocol']),
            host: embedHost
          }).url
        }
      }
    }
    return 'https://viz.swiftcentre.org'
  }

  export const url = async (props: UrlProps) => {
    const {id, type = 'published', ts, query} = props
    const workspace_id = props.type == 'liveView' ? props.workspace_id : undefined
    return uri(type == 'published' ? vizUrl() : env.URL)
      .addPath('results', id, ts?.toString())
      .mergeQuery({
        ...RQ.serializer.serializeToObject({
          ...(type == 'liveView' || !env.NEXT_PUBLIC_VIZ_R2_PARAM ? {} : {r2_url: env.NEXT_PUBLIC_R2_URL}),
          ...query,
          ...(type == 'published' && query?.forecasters ?
            {
              forecasters: await asyncMap(query?.forecasters ?? [], c =>
                Publish.publishedCreatedBy({question_id: id, created_by: c})
              )
            }
          : {})
        }),
        ...(workspace_id ? {workspace: workspace_id} : {})
      }).url
  }

  export const tsToDate = (ts: string) => DateTime.fromMillis(Number.parseInt(ts))

  export const zExportParams = z.object({
    questionIds: z.array(z.string()),
    workspace_id: z.string(),
    includeForecasts: z.boolean().optional(),
    includeComments: z.boolean().optional(),
    anonymizeUsers: z.boolean().optional()
  })
  export type ExportParams = z.infer<typeof zExportParams>
  export const zImportParams = zExportParams.extend({
    importUrl: z.string().optional()
  })
  export type ImportParams = z.infer<typeof zImportParams>
  export const zImportResponse = z.object({
    importedQuestions: z.array(z.object({id: z.string(), title: z.string()}))
  })

  export const exportQuestionUrl = (options: ExportParams) =>
    uri('/api/question-export', zExportParams).mergeQuery(options).url

  export const importQuestion = async ({
    file,
    ...options
  }: {
    file: File
  } & ImportParams) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await uri('/api/question-import', zImportParams)
      .mergeQuery(queryStringSerializer(zImportParams).serializeToObject(options))
      .fetch({method: 'POST', body: formData})
      .then(r => processFetchJsonResponse(r, zImportResponse))
    return response
  }
  export type ImportQuestionResult = z.infer<typeof zImportResponse>

  export const importQuestionFromUrl = async (options: ImportParams & {importUrl: string}) => {
    const response = await uri('/api/question-import', zImportParams)
      .mergeQuery(queryStringSerializer(zImportParams).serializeToObject(options))
      .fetch({method: 'POST'})
      .then(r => processFetchJsonResponse(r, zImportResponse))
    return response
  }

  /** makes a short hash of the question_id and created_by so we can hide the original forecasters from consumers of reports **/
  export const publishedCreatedBy = ({question_id, created_by}: {question_id: string; created_by: string}) =>
    hashString(`${created_by}|${question_id} `, {length: 10, salt: 'juG5AMcmLQ49lLl&'})
}
